import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/globals.css';
import App from './App';
import { ThemeProvider } from 'next-themes';
import { GlobalStateProvider } from '../components/GlobalStateProvider';
import ConsentBanner from '../components/ConsentBanner';
import { initSentry, Sentry } from './lib/sentry';
import { initPostHog } from './lib/posthog';

// Initialize Sentry before anything else
initSentry();
// Product analytics + heatmaps (no-ops without VITE_PUBLIC_POSTHOG_KEY / non-prod)
initPostHog();

// --- Global watchdog: defuse lingering inert / scroll-lock artifacts (Radix/RemoveScroll) ---
(function setupDefuse() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // Temporarily disabled to avoid interfering with React DOM lifecycles
  return;

  const defuse = () => {
    try {
      // 1) Inert blocks interaction even if hit-testing returns your element
      document.querySelectorAll('[inert]').forEach((el) => el.removeAttribute('inert'));

      // 2) Clear scroll-lock flags/classes
      document.documentElement.removeAttribute('data-radix-scroll-lock');
      document.body.removeAttribute('data-radix-scroll-lock');
      document.body.classList.remove('react-remove-scroll-body');

      // 3) Normalize any inline locks
      const html = document.documentElement as HTMLElement;
      const body = document.body as HTMLElement;
      html.style.overflow = '';
      body.style.overflow = '';
      body.style.position = '';
      body.style.width = '';
      body.style.paddingRight = '';
    } catch {
      // OBS-11 a-ignore: best-effort cleanup of stuck Radix overlays/locks; the nodes may already be gone.
    }
  };

  // run immediately
  defuse();

  // run on navigation / focus changes
  ['visibilitychange', 'focus', 'popstate'].forEach((evt) => {
    window.addEventListener(evt as any, defuse, { capture: true });
  });

  // run right after user input; lets portals finish closing first
  ['pointerdown', 'keydown'].forEach((evt) => {
    window.addEventListener(evt as any, () => setTimeout(defuse, 0), { capture: true });
  });

  // run whenever the DOM tries to (re)apply locks
  const mo = new MutationObserver(() => defuse());
  try {
    mo.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ['inert', 'data-radix-scroll-lock', 'class', 'style'],
    });
  } catch {
    // OBS-11 a-ignore: best-effort cleanup of stuck Radix overlays/locks; the nodes may already be gone.
  }
})();

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: unknown }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error: unknown) {
    return { error };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Send to Sentry
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
        source: 'RootErrorBoundary',
      },
    });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 24, color: '#fff', background: '#300', minHeight: '100vh' }}>
          <h1>Runtime error ⚠️</h1>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{String(this.state.error)}</pre>
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: 16, padding: '8px 16px', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem storageKey="coachd:theme">
      <GlobalStateProvider>
        <App />
        <ConsentBanner />
      </GlobalStateProvider>
    </ThemeProvider>
  </ErrorBoundary>,
);

// Dev-only: Expose test functions globally
if (import.meta.env.DEV) {
  import('./dev/testFeatureFlag')
    .then((module) => {
      (window as any).testFeatureFlag = module.testFeatureFlag;
      console.log('🧪 Feature flag test function available: window.testFeatureFlag()');
    })
    // OBS-11 a-ignore: dev-only helpers attached to window; absent from some builds by design.
    .catch(() => {});

  import('./dev/sentryCanary')
    .then((module) => {
      (window as any).sentryCanary = module.sentryCanary;
      console.log('🧪 Sentry canary available: window.sentryCanary() or window.sentryCanary("worker")');
    })
    // OBS-11 a-ignore: dev-only helpers attached to window; absent from some builds by design.
    .catch(() => {});
}
